import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Report.css";

const Report = () => {
  const [startDate, setStartDate] = useState("2016-05-17");
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  // Validate if date is in YYYY-MM-DD format and valid
  const isValidDate = (dateStr) => {
    if (!dateStr) return false;
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    if (!regex.test(dateStr)) return false;
    const [year, month, day] = dateStr.split("-");
    const date = new Date(year, month - 1, day);
    return date && date.getFullYear() === parseInt(year, 10);
  };

  // Fetch data only when triggered manually
  const fetchReportData = async () => {
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      setError("Please enter valid start and end dates in YYYY-MM-DD format.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/get-performance-report`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      const data = response.data;
      if (!data || Object.keys(data).length === 0) {
        setError("No data available for the selected date range.");
        setReportData(null);
      } else if (!data.metrics || !data.plots) {
        setError("Invalid report data format from server.");
        setReportData(null);
      } else {
        console.log("Report data:", data);
        setReportData(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load performance report.");
    } finally {
      setLoading(false);
    }
  };

  // Handle date changes without immediate fetch
  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
    setReportData(null); // Clear data on new input to avoid stale results
    setError(null); // Clear error on new input
  };

  const handleDownloadPDF = async () => {
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      setError("Please enter valid dates before downloading.");
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/download-performance-report`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "performance_report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError("Failed to download PDF: " + (err.response?.data?.detail || "Unknown error"));
    }
  };

  // Initial fetch with default dates
  useEffect(() => {
    fetchReportData();
  }, []); // Empty dependency array for initial load only

  if (loading) return <div>Loading report...</div>;
  if (error) return (
    <div>
      <p>Error: {error}</p>
      <button onClick={() => fetchReportData()}>Retry</button>
    </div>
  );
  if (!reportData || !reportData.metrics || !reportData.plots) {
    return (
      <div>
        <p>No report data available for the selected date range.</p>
        <button onClick={fetchReportData}>Generate Report</button>
      </div>
    );
  }

  const { metrics, plots, dates } = reportData;

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "N/A";
    return Number(value).toFixed(2);
  };

  return (
    <div className="report-container report-page">
      <h1>Performance Report</h1>
      <div className="date-range-container">
        <div className="date-picker">
          <label>START DATE: </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate}
            min="2016-05-17"
          />
        </div>
        <div className="date-picker">
          <label>END DATE: </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={new Date().toISOString().split("T")[0]}  // Limits to today
          />
        </div>
        <button onClick={fetchReportData}>Generate Report</button>
      </div>
      <button onClick={handleDownloadPDF}>Download PDF</button>

      {/* Summary Metrics Section */}
      <h2>Summary Metrics</h2>
      <table className="metrics-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Fund</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Start Period</td>
            <td>{metrics.start_period || "N/A"}</td>
          </tr>
          <tr>
            <td>End Period</td>
            <td>{metrics.end_period || "N/A"}</td>
          </tr>
          <tr>
            <td>Cumulative Return</td>
            <td>{formatNumber(metrics.cumulative_return)}%</td>
          </tr>
          <tr>
            <td>CAGR</td>
            <td>{formatNumber(metrics.cagr)}%</td>
          </tr>
          <tr>
            <td>Volatility (Annualized)</td>
            <td>{formatNumber(metrics.volatility)}%</td>
          </tr>
          <tr>
            <td>Sharpe Ratio</td>
            <td>{formatNumber(metrics.sharpe)}</td>
          </tr>
          <tr>
            <td>Sortino Ratio</td>
            <td>{formatNumber(metrics.sortino)}</td>
          </tr>
          <tr>
            <td>Best Year</td>
            <td>{formatNumber(metrics.best_year)}%</td>
          </tr>
          <tr>
            <td>Worst Year</td>
            <td>{formatNumber(metrics.worst_year)}%</td>
          </tr>
          <tr>
            <td>Best Month</td>
            <td>{formatNumber(metrics.best_month)}%</td>
          </tr>
          <tr>
            <td>Worst Month</td>
            <td>{formatNumber(metrics.worst_month)}%</td>
          </tr>
          <tr>
            <td>Skew</td>
            <td>{formatNumber(metrics.skew)}</td>
          </tr>
          <tr>
            <td>Kurtosis</td>
            <td>{formatNumber(metrics.kurtosis)}</td>
          </tr>
          <tr>
            <td>Expected Daily</td>
            <td>{formatNumber(metrics.expected_daily)}%</td>
          </tr>
          <tr>
            <td>Expected Monthly</td>
            <td>{formatNumber(metrics.expected_monthly)}%</td>
          </tr>
          <tr>
            <td>Expected Yearly</td>
            <td>{formatNumber(metrics.expected_yearly)}%</td>
          </tr>
          <tr>
            <td>Profit Factor</td>
            <td>{formatNumber(metrics.profit_factor)}</td>
          </tr>
          <tr>
            <td>Payoff Ratio</td>
            <td>{formatNumber(metrics.payoff_ratio)}</td>
          </tr>
          <tr>
            <td>Common Sense Ratio</td>
            <td>{formatNumber(metrics.common_sense_ratio)}</td>
          </tr>
          <tr>
            <td>Tail Ratio</td>
            <td>{formatNumber(metrics.tail_ratio)}</td>
          </tr>
          <tr>
            <td>3M CAGR</td>
            <td>{formatNumber(metrics["3m_cagr"])}%</td>
          </tr>
          <tr>
            <td>6M CAGR</td>
            <td>{formatNumber(metrics["6m_cagr"])}%</td>
          </tr>
          <tr>
            <td>1y CAGR</td>
            <td>{formatNumber(metrics["1y_cagr"])}%</td>
          </tr>
          <tr>
            <td>3y CAGR</td>
            <td>{formatNumber(metrics["3y_cagr"])}%</td>
          </tr>
          <tr>
            <td>5y CAGR</td>
            <td>{formatNumber(metrics["5y_cagr"])}%</td>
          </tr>
        </tbody>
      </table>

      {/* Plots */}
      {plots.cumulative_returns && (
        <>
          <h2>Cumulative Returns</h2>
          <img src={`data:image/png;base64,${plots.cumulative_returns}`} alt="Cumulative Returns" />
        </>
      )}
      {plots.rolling_volatility && (
        <>
          <h2>Rolling Volatility (6-Months)</h2>
          <img src={`data:image/png;base64,${plots.rolling_volatility}`} alt="Rolling Volatility" />
        </>
      )}
      {reportData.plots.monthly_heatmap && (
        <img
          src={`data:image/png;base64,${reportData.plots.monthly_heatmap}`}
          alt="Monthly Heatmap"
          style={{ width: "100%", height: "auto", marginTop: "20px" }}
        />
      )}
      {plots.rolling_sharpe && (
        <>
          <h2>Rolling Sharpe (6-Months)</h2>
          <img src={`data:image/png;base64,${plots.rolling_sharpe}`} alt="Rolling Sharpe" />
        </>
      )}
      {plots.rolling_sortino && (
        <>
          <h2>Rolling Sortino (6-Months)</h2>
          <img src={`data:image/png;base64,${plots.rolling_sortino}`} alt="Rolling Sortino" />
        </>
      )}

      {/* Disclaimer */}
      <p className="disclaimer">
Performance data reflects actual trading results of Reichelt Capital LLC (the "Fund") beginning 2016-05-17, unless otherwise stated, and is presented net of all fees and expenses. Returns are compared against the Invesco QQQ Trust (QQQ) as a benchmark for illustrative purposes; however, the Fund's strategy differs significantly in volatility, leverage, and composition, and QQQ may not fully reflect the Fund's risk profile. This document is for informational purposes only and does not constitute an offer to sell or a solicitation to buy securities. Clients and prospective investors should carefully review the subscription agreement and operating agreement before making an investment decision. Past performance is not indicative of future results, and all investments involve risk, including the potential loss of principal. Fund performance driven by a volatile strategy and concentrated positions.
      </p>
    </div>
  );
};

export default Report;
